import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Slider from 'react-slick';

import { theme } from '@prose-ui';
import { keyframes, legacyTheme, styled } from '@prose-ui/legacy';
import MarkdownIt from 'markdown-it';

import ProgressLine from 'Components/ProgressLine';

import { scroringModalScoreLabel } from 'assets/content/prescription/content';

import SliderCustomArrow from './SliderCustomArrow';

const ScoringContentOutter = styled('div')`
  border: solid 1px ${theme.colors.tertiary['200']};
  background: ${theme.colors.neutral[100]};
  text-align: left;
  font-size: 0.875rem; // 14px
  font-family: ${legacyTheme.typography.simplonNorm};
  color: ${theme.colors.primary[400]};

  ${legacyTheme.breakpoints.up('lg')} {
    width: 550px !important;
  }
`;

const ScoringContentInner = styled('div')`
  width: 90%;
  height: 330px;
  margin: 0 auto;
`;

const MinMaxLine = styled('div')`
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem; // 12px
  p {
    margin: 0;
    letter-spacing: 0.5px;
  }
`;

const ScoreResultSubtitle = styled('div')`
  text-transform: uppercase;
  font-size: 0.74rem; // 13px
  margin-bottom: 15px;
  font-family: ${legacyTheme.typography.simplonMono};
  font-weight: 600;
  color: ${theme.colors.primary[400]};

  ${legacyTheme.breakpoints.up('md')} {
    margin: 15px 0;
  }
`;

const StyledKeyQuestions = styled.div`
  p,
  ul {
    margin: 0;
    letter-spacing: 0.5px;
  }

  li {
    margin-left: -26px;
  }
  margin-bottom: 30px;
`;

const StyledGoalDescription = styled.div`
  p {
    margin: 20px 0;
    letter-spacing: 0.5px;
  }
`;

const slideInAnimation = keyframes`
  100% {
    transform: translateX(0);
  }
`;

const StyledSlider = styled(Slider)`
  animation: ${slideInAnimation} 0.3s linear forwards;
  transform: translateX(100%);

  .slick-prev {
    width: 36px;
    height: 36px;
    z-index: 1;
    left: -20px;

    svg {
      transform: rotate(180deg);
      margin-bottom: 20px;
    }
    ::before {
      content: '';
    }

    ::after {
      color: ${theme.colors.primary[400]};
      content: '';
    }

    &:hover {
      ::after {
        position: relative;
        background: ${theme.colors.neutral[100]};
        content: '${props => props.previousValue}';
        font-size: 11px;
        font-family: ${legacyTheme.typography.simplonMono};
        font-weight: bold;
        left: 50%;
        transform: translate(-50%, -50%);
        display: inline-block;
        letter-spacing: 0.5px;
        line-height: 19px;
      }
    }
  }

  .slick-next {
    width: 36px;
    height: 36px;
    right: -20px;

    svg {
      margin-bottom: 20px;
    }
    ::before {
      content: '';
    }
    ::after {
      color: ${theme.colors.primary[400]};
      content: '';
    }

    &:hover {
      ::after {
        position: relative;
        background: ${theme.colors.neutral[100]};
        content: '${props => props.nextValue}';
        font-size: 11px;
        font-family: ${legacyTheme.typography.simplonMono};
        font-weight: bold;
        left: 50%;
        transform: translate(-50%, -50%);
        display: inline-block;
        letter-spacing: 0.5px;
        line-height: 19px;
      }
    }
  }

  .slick-disabled {
    svg {
      path:first-of-type {
        fill: ${theme.colors.neutral[700]};
      }
    }

    &:hover {
      svg {
        path:first-of-type {
          fill: ${theme.colors.neutral[700]};
        }
      }
    }
  }

  ${legacyTheme.breakpoints.up('md')} {
    width: 100%;
  }

  .slick-dots li {
    margin: 0 2px;
    width: 7px;
  }
`;

const StyledBackButton = styled.button`
  background: none;
  border: none;
  position: relative;
  left: -168px;
  margin: 5px 0;
  font-family: ${legacyTheme.typography.simplonNorm};
  color: ${theme.colors.primary[400]};
  font-size: 0.875rem; // 14px
  cursor: pointer;
  animation: ${slideInAnimation} 0.3s linear forwards;
  transform: translateX(100%);

  &:hover {
    text-decoration: underline;
  }

  &::before {
    border-style: solid;
    border-width: 1px 1px 0 0;
    content: '';
    display: inline-block;
    height: 0.45em;
    position: relative;
    top: 0.15em;
    vertical-align: top;
    width: 0.45em;
    left: 0.25em;
    transform: rotate(-135deg);
    margin: 0 15px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    left: -180px;
  }

  ${legacyTheme.breakpoints.up('lg')} {
    left: -218px;
  }
`;

const ScoringListContentDesktop = ({ goals, selectedValue, onClose, onScoringChange }) => {
  const md = new MarkdownIt();
  const sliderRef = useRef();
  const [slideIndex, setSlideIndex] = useState(0);
  const [showPrevArrow, setShowPrevArrow] = useState();
  const maxIndex = goals.length - 1 === slideIndex;
  const nextValue = maxIndex ? '' : goals[slideIndex + 1];
  const previousValue = slideIndex > 0 ? goals[slideIndex - 1] : '';

  useEffect(() => {
    // go to the selected slide
    const goalIndex = goals.findIndex(current => current.name === selectedValue);
    sliderRef.current.slickGoTo(goalIndex);
    onScoringChange(selectedValue);
  }, [selectedValue]);

  useEffect(() => {
    // display previous arrow rule
    if (slideIndex > 0) {
      setShowPrevArrow(true);
    } else {
      setShowPrevArrow(false);
    }
  }, [slideIndex]);

  const afterChange = index => {
    // get the displayed goal
    const goal = goals[index];
    onScoringChange(goal.name);
  };

  return (
    <>
      <StyledBackButton onClick={onClose} type="button">
        Back to Scorecard
      </StyledBackButton>
      <StyledSlider
        ref={sliderRef}
        afterChange={afterChange}
        arrows
        beforeChange={(_current, next) => setSlideIndex(next)}
        dots
        fade
        infinite={false}
        nextArrow={<SliderCustomArrow />}
        nextValue={nextValue?.name?.toUpperCase()}
        prevArrow={showPrevArrow && <SliderCustomArrow />}
        previousValue={previousValue?.name?.toUpperCase()}
        slidesToScroll={1}
        slidesToShow={1}
        speed={500}
      >
        {goals?.map(goal => (
          <ScoringContentOutter key={goal.name}>
            <ScoringContentInner>
              <ScoreResultSubtitle>
                {scroringModalScoreLabel(goal.name, goal.value)}
              </ScoreResultSubtitle>
              <ProgressLine
                backgroundColor={theme.colors.primary[100]}
                color={theme.colors.primary[300]}
                height="8px"
                progress={goal.value ?? 0}
              />
              <MinMaxLine>
                <p>{goal.minLabel}</p>
                <p>{goal.maxLabel}</p>
              </MinMaxLine>
              <StyledGoalDescription
                dangerouslySetInnerHTML={{ __html: md.render(goal.description || '') }}
              />
              <StyledKeyQuestions
                dangerouslySetInnerHTML={{ __html: md.render(goal.keyQuestions || '') }}
              />
            </ScoringContentInner>
          </ScoringContentOutter>
        ))}
      </StyledSlider>
    </>
  );
};
ScoringListContentDesktop.defaultProps = {
  selectedValue: '',
};

ScoringListContentDesktop.propTypes = {
  goals: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      name: PropTypes.string,
      minLabel: PropTypes.string,
      maxLabel: PropTypes.string,
      description: PropTypes.string,
      keyQuestions: PropTypes.string,
    })
  ).isRequired,
  selectedValue: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onScoringChange: PropTypes.func.isRequired,
};

export default ScoringListContentDesktop;
